import type { FC } from 'react'
import styled from '@emotion/styled'

import ColumnTrack from '@segment/matcha/components/ColumnTrack'
import Typography from '@segment/matcha/components/Typography'
import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'
import Badge from '@segment/matcha/components/Badge'
import ButtonText from '@segment/matcha/components/ButtonText'

import CardBase, * as Card from '@segment/matcha/recipes/Card'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import type { ResourcesProps } from '../../data'

const ResourcesSection: FC<ResourcesProps> = ({ title, resources }) => {
  return (
    <SectionBase variant="noCopy">
      <Section.Contents>
        <Heading variant="h2" tag="h2">
          {title}
        </Heading>

        <ColumnTrack columns={1} overrides={{ md: 2, lg: 3 }}>
          {resources.map(({ title, asset, type, categories, excerpt, authorLogo, authorName, button, href }) => (
            <CardBase key={title} href={href}>
              <CardImage variant="fill" src={asset} alt="" />
              <Card.Body>
                <Card.Accessories>
                  <Card.AccessoriesRow>
                    <Card.Label>{type}</Card.Label>
                  </Card.AccessoriesRow>
                  {categories && (
                    <Card.AccessoriesRow>
                      {categories.map((category) => (
                        <Badge key={category} variant="outlined">
                          {category}
                        </Badge>
                      ))}
                    </Card.AccessoriesRow>
                  )}
                </Card.Accessories>
                <Card.Copy>
                  <Card.Headline tag="h3">{title}</Card.Headline>
                  <Card.Description>{excerpt}</Card.Description>
                  {authorLogo && authorName && (
                    <Card.AccessoriesRow>
                      <Card.Author src={authorLogo} alt="" size="small" />
                      <Card.Label variant="textTiny">{authorName}</Card.Label>
                    </Card.AccessoriesRow>
                  )}
                </Card.Copy>

                <Card.Action>
                  <ButtonText size="medium" iconEnd={ChevronRightIcon}>
                    {button}
                  </ButtonText>
                </Card.Action>
              </Card.Body>
            </CardBase>
          ))}
        </ColumnTrack>
      </Section.Contents>
    </SectionBase>
  )
}

const Heading = styled(Typography)`
  margin-top: 0;
  margin-bottom: 40px;
`

const CardImage = styled(Card.Image)`
  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    height: 209px;
  }
`

export default ResourcesSection
